import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { StackedDialog } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';

// In-person signing handoff. Given a signing URL (from createSignatureSession
// with inPerson: true), offers two ways to get the customer signing:
//   1. Open the signing page full-screen on this device (tablet handoff).
//   2. Scan a QR code to sign on the customer's own phone.
//
// NOTE on the QR: it's rendered via a public QR-image generator so we add no
// dependencies. That sends the (single-use, 14-day-expiry) signing token to a
// third party. If token secrecy needs to stay fully in-house, swap this <img>
// for a locally-rendered QR (adds the small `qrcode` package) or rely on the
// tablet-handoff button only.
export default function InPersonSignDialog({ open, onClose, signUrl, signerName }) {
  const qrSrc = signUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(signUrl)}`
    : null;

  const openHere = () => { if (signUrl) window.open(signUrl, '_blank', 'noopener'); };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(signUrl);
      StateManager.setAlertAndOpen('Signing link copied.', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen('Could not copy the link.', 'error');
    }
  };

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Sign in person
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {signerName ? <>Ready for <strong>{signerName}</strong> to sign. </> : null}
          Hand over this device, or have the customer scan the code to sign on their phone.
        </Typography>

        <Button fullWidth variant="contained" startIcon={<OpenInNewIcon />} onClick={openHere} sx={{ mb: 2 }}>
          Open signing page on this device
        </Button>

        {qrSrc && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src={qrSrc} alt="Scan to sign" width={220} height={220} style={{ borderRadius: 8 }} />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField value={signUrl || ''} size="small" fullWidth InputProps={{ readOnly: true }} />
          <Tooltip title="Copy link">
            <IconButton onClick={copyLink}><ContentCopyIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </StackedDialog>
  );
}
